import { useEffect, useRef, useState } from 'react';
import { Box } from '@cad-arc';

/**
 * A still first frame of an alert's clip, drawn to a canvas.
 *
 * The alert image is a nine-frame animated gif: about 362 KB on the wire, but roughly 10.5 MB
 * **resident** once the browser has decoded every frame. The alert log holds up to two hundred, and
 * the drawer and the grid rail render one per row, so showing the gif directly in a list is tens to
 * hundreds of megabytes of decoded frames held for as long as the list is open — in a client that
 * runs for whole shifts.
 *
 * `loading="lazy"` does not help: it defers the *fetch*, not the decode, and a row that has been
 * scrolled past stays decoded.
 *
 * So a list row gets one frame. The gif is decoded once into a canvas of thumbnail size, and the
 * source image is dropped immediately — what stays resident is the canvas bitmap, which is a few
 * kilobytes at this size. The animated gif is kept for the sensor event panel, where exactly one
 * is on screen and the motion is the point.
 *
 * This is why neither list is virtualised: virtualising would bound how many gifs are decoded at
 * once, but decoding none of them is better, and a few hundred plain rows is not a problem.
 */
export function SensorAlertThumbnail({
    imageUrl,
    width,
    height,
    fillWidth = false,
    fill = false,
    alt = '',
}: {
    imageUrl: string | undefined;
    /** Intrinsic canvas width in pixels. With `fillWidth` this is the bitmap size, not the box. */
    width: number;
    height: number;
    /**
     * Stretch the canvas to its container's width. The bitmap stays `width` x `height`, so a
     * full-width tile still only holds a small decoded image.
     */
    fillWidth?: boolean;
    /**
     * Fill the container in both directions, for a box that already has its shape — the Sensor
     * Event Card's 16:9 clip. `width` x `height` must share that aspect ratio or the frame will
     * stretch; the draw itself is always cover-cropped.
     */
    fill?: boolean;
    alt?: string;
}): React.ReactElement {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [hasDrawnFrame, setHasDrawnFrame] = useState(false);

    useEffect(
        function drawFirstFrameToCanvas() {
            const canvas = canvasRef.current;
            if (!canvas || !imageUrl) {
                return;
            }

            // Not `new Image()` held in state: the point is for this to be collectable the moment
            // the frame is on the canvas.
            const image = new Image();
            let isCancelled = false;

            image.onload = function drawFrame() {
                if (isCancelled) {
                    return;
                }
                const context = canvas.getContext('2d');
                if (!context) {
                    return;
                }
                /*
                 * Cover, not contain: these are square thumbnails of non-square footage, and a
                 * letterboxed 44px tile shows almost nothing. Crop to the centre instead.
                 */
                const scale = Math.max(width / image.width, height / image.height);
                const drawWidth = image.width * scale;
                const drawHeight = image.height * scale;
                context.drawImage(
                    image,
                    (width - drawWidth) / 2,
                    (height - drawHeight) / 2,
                    drawWidth,
                    drawHeight
                );
                setHasDrawnFrame(true);
            };

            /*
             * The host serves these with a permissive CORP header and no auth, but it 403s on
             * HEAD — so nothing here may preflight. `crossOrigin` is deliberately not set: it
             * would turn this into a CORS request and the canvas would taint anyway on failure.
             * Reading the canvas back is not needed, only drawing to it.
             */
            image.src = imageUrl;

            return function cancelDecode() {
                isCancelled = true;
                // Abandons an in-flight decode and lets the image go.
                image.src = '';
            };
        },
        [imageUrl, width, height]
    );

    return (
        <Box
            width={fill || fillWidth ? '100%' : `${width}px`}
            height={fill ? '100%' : `${height}px`}
            flexShrink={0}
            borderRadius={fill ? undefined : 'sm'}
            overflow="hidden"
            backgroundColor="surface.canvas"
        >
            {imageUrl ? (
                <canvas
                    ref={canvasRef}
                    width={width}
                    height={height}
                    aria-label={alt || undefined}
                    role={alt ? 'img' : undefined}
                    style={{
                        display: 'block',
                        width: fill || fillWidth ? '100%' : `${width}px`,
                        height: fill ? '100%' : `${height}px`,
                        // Until the frame lands, the empty canvas would be a white square.
                        opacity: hasDrawnFrame ? 1 : 0,
                        transition: 'opacity 120ms',
                    }}
                />
            ) : undefined}
        </Box>
    );
}
